import { Link } from "react-router-dom";
import { Satellite, CheckCircle2, Sparkles, ArrowRight } from "lucide-react";
import { useLang } from "../context/LanguageContext";

// VSAT equipment showcase — mirrors the structure of CctvGallery so the
// home flow stays consistent (comparator → equipment gallery → next group).
// Uses the dedicated VSAT photos already shipped under /public/vsat/.

const COPY = {
  pt: {
    overline: "Equipamentos VSAT",
    title: "Antenas, modems e link-end-to-end que instalamos em Angola",
    body: "Antenas de Banda C, Ku e Ka (0,74 m a 3,7 m), modems iDirect, Newtec, Hughes e Gilat, BUC/LNB calibrados e ACU motorizadas. Projetamos o link budget, instalamos no local e operamos com SLA 24/7.",
    capabilities_label: "Capacidades-chave",
    highlights_label: "Onde entregamos",
    full_page_cta: "Ver página completa",
    quote_cta: "Pedir orçamento VSAT",
    photos: [
      "Instalação VSAT em site remoto",
      "Antena Banda C de 2,4 m",
      "Antena offset Ku/Ka de 1,2 m",
      "Modem iDirect 950MP",
    ],
    capabilities: [
      "Link budget completo (uplink/downlink, ACM, fade margin)",
      "Apontamento, alinhamento e cross-pol oficial",
      "Antenas Banda C (1,8–3,7 m) para zonas de chuva tropical",
      "Antenas Ku/Ka (0,74–1,8 m) para sites empresariais",
      "Modems iDirect, Newtec, Hughes, Gilat e Comtech",
      "BUC, LNB, feeds e mount pole calibrados em fábrica",
      "VPN, QoS, aceleração TCP e firewall integrado",
      "Telefonia VoIP sobre VSAT (E1/SIP)",
    ],
    highlights: [
      "Plataformas offshore (petróleo & gás)",
      "Operações mineiras no interior",
      "ONGs e cooperação internacional",
      "Backup de fibra para empresas críticas",
    ],
  },
  en: {
    overline: "VSAT equipment",
    title: "Antennas, modems and end-to-end links we install in Angola",
    body: "C, Ku and Ka-band antennas (0.74 m to 3.7 m), iDirect, Newtec, Hughes and Gilat modems, calibrated BUC/LNBs and motorized ACUs. We design the link budget, install on-site and operate with 24/7 SLA.",
    capabilities_label: "Core capabilities",
    highlights_label: "Where we deliver",
    full_page_cta: "See the full page",
    quote_cta: "Request a VSAT quote",
    photos: [
      "VSAT install at a remote site",
      "2.4 m C-band antenna",
      "1.2 m Ku/Ka offset antenna",
      "iDirect 950MP modem",
    ],
    capabilities: [
      "Full link budget (uplink/downlink, ACM, fade margin)",
      "Pointing, alignment and official cross-pol",
      "C-band antennas (1.8–3.7 m) for tropical rain zones",
      "Ku/Ka antennas (0.74–1.8 m) for business sites",
      "iDirect, Newtec, Hughes, Gilat and Comtech modems",
      "Factory-calibrated BUC, LNB, feeds and mount poles",
      "Built-in VPN, QoS, TCP acceleration and firewall",
      "VoIP telephony over VSAT (E1/SIP)",
    ],
    highlights: [
      "Offshore oil & gas platforms",
      "Inland mining operations",
      "NGOs and international cooperation",
      "Fiber backup for critical companies",
    ],
  },
  es: {
    overline: "Equipos VSAT",
    title: "Antenas, modems y enlaces extremo a extremo que instalamos en Angola",
    body: "Antenas Banda C, Ku y Ka (0,74 m a 3,7 m), modems iDirect, Newtec, Hughes y Gilat, BUC/LNB calibrados y ACU motorizadas. Diseñamos el link budget, instalamos en sitio y operamos con SLA 24/7.",
    capabilities_label: "Capacidades clave",
    highlights_label: "Dónde entregamos",
    full_page_cta: "Ver página completa",
    quote_cta: "Solicitar presupuesto VSAT",
    photos: [
      "Instalación VSAT en sitio remoto",
      "Antena Banda C de 2,4 m",
      "Antena offset Ku/Ka de 1,2 m",
      "Modem iDirect 950MP",
    ],
    capabilities: [
      "Link budget completo (uplink/downlink, ACM, fade margin)",
      "Apuntamiento, alineación y cross-pol oficial",
      "Antenas Banda C (1,8–3,7 m) para zonas tropicales",
      "Antenas Ku/Ka (0,74–1,8 m) para sitios empresariales",
      "Modems iDirect, Newtec, Hughes, Gilat y Comtech",
      "BUC, LNB, feeds y mount pole calibrados en fábrica",
      "VPN, QoS, aceleración TCP y firewall integrado",
      "Telefonía VoIP sobre VSAT (E1/SIP)",
    ],
    highlights: [
      "Plataformas offshore (petróleo y gas)",
      "Operaciones mineras del interior",
      "ONGs y cooperación internacional",
      "Respaldo de fibra para empresas críticas",
    ],
  },
};

const PHOTOS = [
  "/vsat/vsat-remote-site.png",
  "/vsat/vsat-antenna-cband-2m4.png",
  "/vsat/vsat-antenna-1m2.png",
  "/vsat/vsat-modem-idirect-950mp.png",
];

export const VsatGallery = () => {
  const { lang } = useLang();
  const copy = COPY[lang] || COPY.pt;

  return (
    <section
      id="vsat-equipment"
      data-testid="vsat-equipment"
      className="relative scroll-mt-24 border-t border-slate-200 bg-gradient-to-b from-[#0a1730] via-[#0b1d3a] to-[#0a1730] py-20 text-white lg:py-24"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
        {/* Header */}
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.28em] text-amber-300">
              <Satellite className="h-3.5 w-3.5" strokeWidth={2.5} />
              {copy.overline}
            </div>
            <h2
              data-testid="vsat-equipment-title"
              className="font-heading mt-4 text-3xl font-black tracking-tight text-white md:text-4xl lg:text-5xl"
            >
              {copy.title}
            </h2>
            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-slate-300 md:text-base">
              {copy.body}
            </p>
          </div>
          <div className="flex items-end lg:col-span-5 lg:justify-end">
            <div className="flex flex-wrap gap-3">
              <Link
                to="/servicos/comunicacoes"
                data-testid="vsat-equipment-full-page-cta"
                className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-5 py-2.5 text-sm font-semibold text-white backdrop-blur transition-all hover:-translate-y-0.5 hover:border-amber-400/60 hover:bg-white/10"
              >
                {copy.full_page_cta}
                <ArrowRight className="h-4 w-4" strokeWidth={2.5} />
              </Link>
              <a
                href="/#contact"
                data-testid="vsat-equipment-quote-cta"
                className="inline-flex items-center gap-2 rounded-full bg-amber-400 px-5 py-2.5 text-sm font-bold text-slate-900 shadow-md transition-all hover:-translate-y-0.5 hover:bg-amber-300"
              >
                {copy.quote_cta}
                <ArrowRight className="h-4 w-4" strokeWidth={2.5} />
              </a>
            </div>
          </div>
        </div>

        {/* Photo gallery — mosaic, hero spans 2x2 */}
        <div
          data-testid="vsat-equipment-gallery"
          className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4"
        >
          {PHOTOS.map((src, i) => (
            <div
              key={src}
              className={`group relative overflow-hidden rounded-2xl border border-white/10 bg-slate-900/40 shadow-xl ${
                i === 0 ? "sm:col-span-2 sm:row-span-2" : ""
              }`}
            >
              <div className="relative aspect-[4/3]">
                <img
                  src={src}
                  alt={copy.photos[i] || ""}
                  loading="lazy"
                  className={`h-full w-full transition-transform duration-700 group-hover:scale-105 ${
                    i === 0 ? "object-cover" : "object-contain p-6"
                  }`}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-slate-900/10 to-transparent" />
                <div className="pointer-events-none absolute left-4 top-4 rounded-full border border-white/20 bg-black/45 px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.25em] text-amber-200 backdrop-blur">
                  {String(i + 1).padStart(2, "0")} · VSAT
                </div>
                <div className="absolute inset-x-0 bottom-0 p-4">
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-amber-200/80">
                    {copy.photos[i]}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Capabilities + Highlights two-column */}
        <div className="mt-12 grid grid-cols-1 gap-6 lg:grid-cols-12 lg:gap-8">
          <div className="rounded-3xl border border-white/10 bg-slate-900/60 p-6 backdrop-blur-sm md:p-8 lg:col-span-7">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-amber-300/80">
              {copy.capabilities_label}
            </div>
            <ul className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {copy.capabilities.map((it) => (
                <li
                  key={it}
                  className="flex items-start gap-2.5 rounded-xl border border-white/10 bg-white/5 p-3.5 text-[13px] leading-snug text-slate-100"
                >
                  <CheckCircle2
                    className="mt-0.5 h-4 w-4 shrink-0 text-amber-300"
                    strokeWidth={2.5}
                  />
                  <span>{it}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-3xl border border-amber-400/20 bg-gradient-to-br from-amber-950/20 via-slate-900/60 to-slate-900/60 p-6 backdrop-blur-sm md:p-8 lg:col-span-5">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-amber-300/80">
              {copy.highlights_label}
            </div>
            <ul className="mt-4 space-y-3">
              {copy.highlights.map((h) => (
                <li
                  key={h}
                  className="flex items-start gap-2.5 text-sm leading-relaxed text-slate-100"
                >
                  <Sparkles
                    className="mt-0.5 h-4 w-4 shrink-0 text-amber-300"
                    strokeWidth={2.5}
                  />
                  <span>{h}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

export default VsatGallery;
